import {
  Box,
  Image,
  ListContent,
  ListItem,
  ListMedia,
  Text,
} from '@redbubble/design-system';
import React, { useMemo } from 'react';
import {
  MatureContentCover,
  MatureContentCoverStyle,
} from '../../../../components/MatureContentCover';
import { Price } from '../../../../components/Price';
import type { InventoryItem } from '../../../../types/shop-bff/types';
import styles from './ProductStyleSwitcher.module.scss';

type ProductStyleItemVariant = 'buttonStyle' | 'linkStyle';

const PREVIEW_THUMBNAIL_SIZE = 64; // px

export interface PriceProps
  extends Pick<
    NonNullable<InventoryItem['price']>,
    'discount' | 'amount' | 'currency'
  > {}

export interface ProductStyleSelectorItemProps {
  description: string;
  active: boolean;
  longDescription: string;
  variant?: ProductStyleItemVariant;
  previewImageUrl?: string | null;
  matureContent?: boolean;
  descriptionTextType?: string;
  longDescriptionTextType?: string;
  price?: PriceProps | null;
  productPageUrl?: string;
  onClick?: () => void;
}

export const ProductStyleSelectorItem = ({
  active,
  description,
  longDescription,
  productPageUrl,
  previewImageUrl,
  matureContent,
  variant = 'linkStyle',
  descriptionTextType = 'display5',
  longDescriptionTextType = 'body2',
  price,
  onClick,
}: ProductStyleSelectorItemProps) => {
  const itemStyle = useMemo(() => {
    const classNames = [styles.listItem];

    if (active) {
      classNames.push(styles.listItemSelected);
    } else if (variant === 'buttonStyle') {
      classNames.push(styles.listItemButton);
    }

    return classNames.join(' ');
  }, [active, variant]);

  return (
    <ListItem
      className={itemStyle}
      element={productPageUrl ? 'a' : 'div'}
      href={productPageUrl}
      onClick={onClick}
      data-testid="product-style-selector-item"
    >
      <ListMedia>
        <Box
          style={{
            width: PREVIEW_THUMBNAIL_SIZE,
          }}
        >
          <MatureContentCover
            matureContent={matureContent}
            aspectRatioHeight={1}
            styleType={MatureContentCoverStyle.EyesOnly}
          >
            <Image
              alt={description}
              roundedCorners
              src={previewImageUrl}
              width={PREVIEW_THUMBNAIL_SIZE}
              height={PREVIEW_THUMBNAIL_SIZE}
            />
          </MatureContentCover>
        </Box>
      </ListMedia>
      <ListContent className={styles.styleSelectListContent}>
        <Box flex="1" marginRight="m">
          <Text display="block" type={descriptionTextType}>
            {description}
          </Text>
          <Text type={longDescriptionTextType}>{longDescription}</Text>
        </Box>

        {price && (
          <Price
            size="xsmall"
            textAlign="right"
            discount={price.discount}
            amount={price.amount}
            currency={price.currency}
            hideVATOverride
          />
        )}
      </ListContent>
    </ListItem>
  );
};
